<template>
  <div class="min-h-screen py-0 px-0  sm:px-0 lg:px-0">
    <!-- Header Section -->
    <div class="max-w-7xl mx-auto">
      <div class="bg-white rounded-lg p-0 mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900 flex items-center">
              Deposits Management
            </h1>
            <p class="mt-2 text-sm text-gray-600">
              Track and manage all agent deposits with advanced filtering
            </p>
          </div>
          <div class="flex items-center space-x-3">
            <button
              @click="toggleFilters"
              :class="[
                'flex items-center space-x-2 px-2 py-1 rounded-lg font-medium transition-all duration-200',
                showFilters
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              <i class="bx bx-filter-alt text-xl"></i>
              <span>Filters</span>
              <span v-if="activeFiltersCount > 0" class="bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {{ activeFiltersCount }}
              </span>
            </button>
          </div>
        </div>
      </div>


      <!-- Filter Panel -->
      <transition name="slide-fade">
        <div v-if="showFilters" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center">
              <i class="bx bx-filter text-blue-600 mr-2 text-xl"></i>
              Filter Deposits
            </h3>
            <button
              v-if="activeFiltersCount > 0"
              @click="clearAllFilters"
              class="text-sm text-red-600 hover:text-red-700 font-medium flex items-center space-x-1"
            >
              <i class="bx bx-x text-lg"></i>
              <span>Clear All</span>
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <!-- Date Range Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="bx bx-calendar mr-1"></i>
                From Date
              </label>
              <input
                v-model="filters.date_from"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="bx bx-calendar mr-1"></i>
                To Date
              </label>
              <input
                v-model="filters.date_to"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            <!-- Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="bx bx-check-shield mr-1"></i>
                Status
              </label>
              <select
                v-model="filters.status"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="">All Statuses</option>
                <option value="deposited">Deposited</option>
                <option value="reversed">Reversed</option>
              </select>
            </div>

            <!-- Agent Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="bx bx-user mr-1"></i>
                Agent
              </label>
              <input
                v-model="filters.agent"
                type="text"
                placeholder="Search by agent name"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            <!-- Deposited By Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="bx bx-user-check mr-1"></i>
                Deposited By
              </label>
              <input
                v-model="filters.deposited_by"
                type="text"
                placeholder="Search by depositor"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          <!-- Filter Actions -->
          <div class="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              @click="clearAllFilters"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all"
            >
              Clear
            </button>
            <button
              @click="applyFilters"
              :disabled="isApplyingFilters"
              class="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center space-x-2"
            >
              <i v-if="!isApplyingFilters" class="bx bx-search-alt text-lg"></i>
              <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ isApplyingFilters ? 'Applying...' : 'Apply Filters' }}</span>
            </button>
          </div>
        </div>
      </transition>

      <!-- Active Filters Display -->
      <div v-if="activeFiltersCount > 0 && !showFilters" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2 flex-wrap">
            <span class="text-sm font-medium text-blue-900">Active Filters:</span>
            <span v-if="filters.date_from" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              From: {{ filters.date_from }}
              <button @click="clearFilter('date_from')" class="ml-2 hover:text-blue-900">
                <i class="bx bx-x text-sm"></i>
              </button>
            </span>
            <span v-if="filters.date_to" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              To: {{ filters.date_to }}
              <button @click="clearFilter('date_to')" class="ml-2 hover:text-blue-900">
                <i class="bx bx-x text-sm"></i>
              </button>
            </span>
            <span v-if="filters.status" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Status: {{ filters.status }}
              <button @click="clearFilter('status')" class="ml-2 hover:text-blue-900">
                <i class="bx bx-x text-sm"></i>
              </button>
            </span>
            <span v-if="filters.agent" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Agent: {{ filters.agent }}
              <button @click="clearFilter('agent')" class="ml-2 hover:text-blue-900">
                <i class="bx bx-x text-sm"></i>
              </button>
            </span>
            <span v-if="filters.deposited_by" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Deposited By: {{ filters.deposited_by }}
              <button @click="clearFilter('deposited_by')" class="ml-2 hover:text-blue-900">
                <i class="bx bx-x text-sm"></i>
              </button>
            </span>
          </div>
          <button
            @click="clearAllFilters"
            class="text-sm text-blue-700 hover:text-blue-800 font-medium"
          >
            Clear All
          </button>
        </div>
      </div>

      <!-- Deposits Table -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <chapman-agents 
          v-if="currentTap == 'chapmanagents'" 
          :filters="appliedFilters"
          @statistics-updated="updateStatistics"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ChapmanAgents from "./ChapmanAgents.vue";

import { mapGetters } from "vuex";
import checkPermissionExist from "../../mixins/checkPermissionExist";

export default {
  components: { ChapmanAgents },
  middleware: "auth",
  layout: "default",

  mixins: [checkPermissionExist],
  
  data: () => ({
    currentTap: "chapmanagents",
    branches: null,
    showFilters: false,
    isApplyingFilters: false,
    isRefreshing: false,
    statistics: null,
    filters: {
      date_from: '',
      date_to: '',
      status: '',
      agent: '',
      deposited_by: '',
    },
    appliedFilters: {},
  }),

  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    activeFiltersCount() {
      let count = 0;
      if (this.filters.date_from) count++;
      if (this.filters.date_to) count++;
      if (this.filters.status) count++;
      if (this.filters.agent) count++;
      if (this.filters.deposited_by) count++;
      return count;
    },
  },

  created() {
    if (!this.checkPermissionExist(this.user.data, "deposit-read")) {
      this.$router.go(-1);
      return false;
    }
  },

  methods: {
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    applyFilters() {
      this.isApplyingFilters = true;
      this.appliedFilters = { ...this.filters };
      
      setTimeout(() => {
        this.isApplyingFilters = false;
      }, 500);
    },

    clearFilter(filterKey) {
      this.filters[filterKey] = '';
      this.applyFilters();
    },

    clearAllFilters() {
      this.filters = {
        date_from: '',
        date_to: '',
        status: '',
        agent: '',
        deposited_by: '',
      };
      this.appliedFilters = {};
    },

    async refreshData() {
      this.isRefreshing = true;
      this.appliedFilters = { ...this.appliedFilters };
      
      setTimeout(() => {
        this.isRefreshing = false;
      }, 1000);
    },

    updateStatistics(stats) {
      this.statistics = stats;
    },
  },
};
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
